/**
 * Tokens do Design System ePonto (épico JOR4-7341 — Redesign).
 *
 * Fonte: protótipo https://redesign-do-eponto.vercel.app/ e o projeto de Design
 * System (foundations/colors, typography, spacing).
 *
 * Este módulo é o lugar canônico dos tokens — nasceu como
 * `componentes-padrao/designSystem.ts` no layout global (JOR4-7365), passou a
 * `design-system/tokens.ts` na migração das telas de Configurações da Jornada
 * (JOR4-7590) e absorveu os tokens que a migração dos cadastros mantinha em
 * `cadastros/novidades/novidadeMeta.ts`. Telas individuais não redeclaram hex
 * nem font-family: importam daqui, por `componentes-padrao/design-system`.
 */

/**
 * Fonte do Design System. Precisa ser aplicada via `style` no HTML e via
 * `elementAttr` nos widgets DevExtreme, porque o tema define font-family em
 * `.dx-widget` e só o estilo inline o sobrepõe. A Inter já é carregada no
 * index.html.
 *
 * O valor é a variável `--ds-font-family` (css/jornada.css), e não a lista de
 * fontes: assim as regras de CSS do padrão e estes tokens não saem do lugar uma
 * em relação à outra.
 */
export const FONT_FAMILY = "var(--ds-font-family)";

export const fontElementAttr = { style: `font-family: ${FONT_FAMILY};` };

/**
 * Atributos da grid: a fonte do DS mais `datagrid-padrao-ds` (css/jornada.css),
 * que reproduz a tabela do protótipo — contêiner arredondado com borda e sombra,
 * cabeçalho verde, linha de filtro branca e corpo em slate-600.
 */
export const gridElementAttr = {
    class: "datagrid-padrao-ds",
    style: `font-family: ${FONT_FAMILY};`,
};

/*
 * Cromia de marca. O verde do protótipo (#064635) e o verde-claro (#1cb987) são
 * exatamente os tokens do tema DevExtreme (css/dx_generic_cores_raster.css), e o
 * White Label os reescreve em runtime (services/user-theme/index.js). Por isso
 * usamos as variáveis, nunca o hex: cravar o valor faria a tela ignorar o tema
 * do cliente.
 */
export const BRAND = "var(--dx-theme-primary-color)";
export const BRAND_TEXT = "var(--dx-theme-primary-text-color)";
export const BRAND_LIGHT = "var(--dx-theme-secondary-color)";

/*
 * Tokens derivados da marca, publicados em :root por services/user-theme.
 * Existem porque o protótipo usa tons que o tema não expõe: o verde-escuro do
 * menu lateral (#043528) e o verde-claro do item ativo (#4fd6a8). Derivar em vez
 * de cravar mantém o menu coerente em qualquer White Label. Ver
 * services/user-theme/tokensDerivados.ts.
 */
export const MENU_FUNDO = "var(--eponto-sidebar-bg)";
export const MENU_ATIVO_TEXTO = "var(--eponto-sidebar-active-fg)";

/**
 * Separadores internos do menu. Branco com alfa, como no protótipo — e não uma
 * cor derivada da marca, que estoura em marcas claras.
 */
export const MENU_BORDA = "rgba(255, 255, 255, 0.1)";

/**
 * Componentes RGB da marca, para compor cores com alfa em CSS —
 * `rgba(var(--eponto-secondary-rgb), .2)` reproduz o `bg-[#1cb987]/20` do
 * protótipo sem cravar o hex.
 */
export const BRAND_RGB = "var(--eponto-primary-rgb)";
export const BRAND_SECONDARY_RGB = "var(--eponto-secondary-rgb)";

/*
 * Neutros, superfícies e vermelho de validação: escala slate, hex fixo de
 * propósito. Não acompanham a marca — precisam continuar legíveis e
 * distinguíveis em qualquer White Label.
 */
export const TEXTO_TITULO = "#334155";
export const TEXTO_LABEL = "#475569";
export const TEXTO_AUXILIAR = "#94a3b8";
export const BORDA = "#e2e8f0";
export const BORDA_SUAVE = "#f1f5f9";
export const VERMELHO = "#ef4444";

/** Branco dos cards e cinza-esverdeado do fundo das telas (--surface-2). */
export const SUPERFICIE = "#ffffff";
/** Fundo de tela e de linha alternada (surface-2 do protótipo). */
export const SUPERFICIE_2 = "#f7faf9";

/** Sombra padrão de card/contêiner do protótipo. */
export const SOMBRA_CARD =
    "0 1px 3px rgba(0, 0, 0, 0.1), 0 1px 2px -1px rgba(0, 0, 0, 0.1)";

/** Raios de canto. 12px em cards e botões, 16px em modais, 8px em controles. */
export const RAIO_CARD = 12;
export const RAIO_MODAL = 16;
export const RAIO_CONTROLE = 8;

/** Tamanhos de página do rodapé de grid, conforme o Design System. */
export const TAMANHOS_PAGINA = [20, 40, 60, 80, 100];

/*
 * Medidas do layout global (JOR4-7365). Ficam aqui, e não espalhadas em CSS,
 * porque MenuNavegacao precisa delas em JS para calcular a altura do conteúdo.
 */
/*
 * O protótipo desenha 224px (w-56). Passou a 260px para o rótulo das telas de
 * nome longo caber sem reticências. Precisa andar junto com `.menu-ds-raiz` em
 * css/menu-ds.css: este valor é o `maxSize` do dxDrawer, e aquele é a largura do
 * elemento que o drawer mede. Mudar só um dos dois deixa uma faixa de fundo ao
 * lado do menu ou o painel cortado.
 *
 * Não afeta o submenu flutuante do modo colapsado: ele se dimensiona sozinho
 * (min-width/max-width próprios) e se posiciona pelo retângulo do ícone do rail.
 */
export const LARGURA_MENU_EXPANDIDO = 260;
export const LARGURA_MENU_COLAPSADO = 56;
export const ALTURA_TOPBAR = 48;
export const ALTURA_BARRA_ABAS = 36;

/**
 * Toolbar antigo, que o mobile ainda usa: uma barra de 36px com 5px de padding
 * em cima e embaixo (css/basicas.css, #toolbar-buttons).
 */
export const ALTURA_TOOLBAR_MOBILE = 46;
